import { ReadonlyVec3, vec3 } from 'gl-matrix';
import { useRef } from 'react';
import { useActiveEditTarget } from '~/hooks/target/useActiveEditTarget';
import { useEnvironmentViews } from '~/hooks/environment/useEnvironmentViews';
import type { Environment } from '~/engine/data/Environment';
import { towardPositive } from './sliceSweep';

export interface SliceSweepTarget {
    /**
     * The Environment whose displayed slice a sweep moves, the one that
     * holds the active structure set; undefined while no row holds it.
     */
    environment: Environment | undefined;
    /**
     * Unit direction the slice moves along: the slice axis of the
     * Environment's image grid, the axis its slices were acquired along,
     * pointing the positive patient way; undefined while the Environment
     * is not shown or has no image. It keeps its identity while the grid
     * keeps its slice axis, so an effect can key on it.
     */
    axis: ReadonlyVec3 | undefined;
    /**
     * Whether any view shows the Environment, so that a move of its plane
     * intersection point is seen.
     */
    shown: boolean;
}

/**
 * `direction`, or the previous one while the two are equal, so the axis
 * keeps its identity across renders that read the same direction.
 */
function useHeldDirection(direction: vec3 | undefined): vec3 | undefined {
    const held = useRef(direction);
    const same =
        held.current === direction ||
        (held.current !== undefined &&
            direction !== undefined &&
            vec3.exactEquals(held.current, direction));
    if (!same) {
        held.current = direction;
    }
    return held.current;
}

/**
 * Where a sweep of the displayed slice acts: the Environment that holds the
 * active structure set. Its plane intersection point is the displayed
 * slice, and every view of it, in any row, cuts through that point. The
 * axis is the slice axis of the Environment's image grid, whatever plane
 * its views show and however they are ordered or turned. Without an
 * Environment, a view of it or an image there is nothing to sweep, and the
 * target says so.
 */
export function useSliceSweepTarget(): SliceSweepTarget {
    const { environment } = useActiveEditTarget();
    const shown = useEnvironmentViews(environment).length > 0;
    const sliceAxis = shown
        ? environment?.image?.parent.grid.getUnitZ()
        : undefined;

    const axis = useHeldDirection(
        sliceAxis ? towardPositive(sliceAxis) : undefined
    );
    return { environment, axis, shown };
}
